/**
 * Twins LLM Service — the detect_cluster behavior's model call in the browser.
 *
 * The job, prompt, parse, merge and switches are the shared core (one copy
 * with the server runner): supabase/functions/_shared/clusterCore.ts. The
 * workspace prompt is an overlay on the built-in contract through the
 * instruction stack, never a replacement (SPEC-custom-agent-template.md §2 H).
 *
 * Batch size: 40 items per model call. Multiple batches per run if >40 items;
 * results are merged and de-duped by item-id overlap.
 */

import {
  buildClusterPrompt,
  buildClusterSystemPrompt,
  CLUSTER_BATCH_SIZE,
  CLUSTER_JOB,
  mergeClusters,
  parseClusterResponse,
  type ClusterItemInput,
  type ClusterReferenceDoc,
  type ClusterResult,
} from '../../supabase/functions/_shared/clusterCore.ts';
import { resolveModelParams } from '../../supabase/functions/_shared/estimateValueCore.ts';
import { AgentLlmError, callAgentLlm } from './llmClient';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

export const TWINS_BATCH_SIZE = CLUSTER_BATCH_SIZE;
export type TwinsItemInput = ClusterItemInput;
export type TwinsClusterResult = ClusterResult;

export interface TwinsInput {
  items: TwinsItemInput[];
  model?: string;
  /** The workspace's prompt overlay — it specializes the contract, never replaces it. */
  systemPrompt?: string | null;
  /** agent_settings.model_params, held inside the behavior's bound. */
  modelParams?: Record<string, unknown> | null;
  referenceDocs?: ClusterReferenceDoc[];
  timeoutMs?: number;
}

/**
 * Run the cluster behavior on a set of items: sub-batches of TWINS_BATCH_SIZE,
 * one model call each, then the groups merged by shared items. Throws
 * {@link AgentLlmError} when a call fails or its reply is not a cluster list.
 */
export async function runTwinsAgent(input: TwinsInput): Promise<TwinsClusterResult[]> {
  if (!input.items.length) return [];

  const model = input.model ?? DEFAULT_MODEL;
  const params = resolveModelParams(CLUSTER_JOB.modelDefaults, input.modelParams ?? null);
  const { systemPrompt } = buildClusterSystemPrompt({ overlay: input.systemPrompt ?? null, language: params.language });
  const allClusters: TwinsClusterResult[] = [];

  for (let offset = 0; offset < input.items.length; offset += TWINS_BATCH_SIZE) {
    const batch = input.items.slice(offset, offset + TWINS_BATCH_SIZE);
    if (!batch.length) continue;

    const content = await callAgentLlm({
      userPrompt: buildClusterPrompt(batch, input.referenceDocs ?? []),
      systemPrompt,
      model,
      conversationId: `twins-${Date.now()}-${offset}`,
      maxTokens: params.maxTokens,
      temperature: params.temperature,
      timeoutMs: input.timeoutMs ?? 60_000,
    });

    const batchClusters = parseClusterResponse(content, new Set(batch.map((i) => i.id)));
    if (!batchClusters) throw new AgentLlmError('The Twins reply was not a list of clusters.');
    allClusters.push(...batchClusters);
  }

  return mergeClusters(allClusters);
}
